import { Badge } from "@/components/ui/badge";

const STAGE_STYLES = {
  awaiting_assessment: "text-amber-800 bg-amber-100 border-amber-300",
  assessment_completed: "text-sky-800 bg-sky-100 border-sky-300",
  invited: "text-purple-800 bg-purple-100 border-purple-300",
  recommended: "text-emerald-800 bg-emerald-100 border-emerald-300",
  hold: "text-amber-800 bg-amber-100 border-amber-300",
  declined: "text-slate-600 bg-slate-100 border-slate-300",
  approved_trainee: "text-green-800 bg-green-100 border-green-300",
};

export function StatusPill({ stage, label }) {
  if (!stage) return <span className="text-muted-foreground text-xs">—</span>;
  const cls = STAGE_STYLES[stage] || "text-slate-600 bg-slate-100 border-slate-300";
  return (
    <Badge variant="outline" className={`font-mono text-[10px] uppercase tracking-widest ${cls}`} data-testid={`status-pill-${stage}`}>
      {label || stage.replace(/_/g, " ")}
    </Badge>
  );
}

export function ScorePill({ value, suffix = "", testid }) {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground text-xs" data-testid={testid}>—</span>;
  }
  const n = Number(value);
  const color = n >= 80 ? "text-emerald-700" : n >= 65 ? "text-lomond" : n >= 55 ? "text-amber-700" : "text-red-600";
  return <span className={`font-mono font-bold ${color}`} data-testid={testid}>{value}{suffix}</span>;
}

export function ScoreBar({ label, pct, testid }) {
  const n = Number(pct) || 0;
  const color = n >= 80 ? "bg-emerald-500" : n >= 65 ? "bg-lomond" : n >= 55 ? "bg-amber-400" : "bg-red-500";
  return (
    <div data-testid={testid}>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-slate-600">{label}</span>
        <span className="font-mono text-slate-500">{pct}%</span>
      </div>
      <div className="h-2 bg-elevated rounded-sm overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${Math.min(n, 100)}%` }} />
      </div>
    </div>
  );
}

export const OPERATOR_JOURNEY = [
  "Assessment Complete",
  "Selected for Academy",
  "10-Day Lomond Training Camp",
  "External 360 Training",
  "Qualified Development Operator",
  "Live Experience",
  "Lomond Competence",
];

export function BandBadge({ band, overridden }) {
  if (!band) return null;
  const colors = {
    "Strong Candidate": "text-emerald-800 bg-emerald-100 border-emerald-300",
    "Suitable": "text-amber-900 bg-lomond/25 border-lomond",
    "Review / Further Assessment": "text-amber-800 bg-amber-100 border-amber-300",
    "Not Recommended": "text-red-700 bg-red-100 border-red-300",
  };
  return (
    <span className="inline-flex items-center gap-2">
      <Badge variant="outline" className={`font-mono text-[10px] uppercase tracking-widest ${colors[band] || ""}`} data-testid="band-badge">
        {band}
      </Badge>
      {overridden && (
        <span className="text-[10px] text-amber-700 font-mono uppercase" data-testid="safety-override-flag">Safety override</span>
      )}
    </span>
  );
}

export const BOOKING_STATUS_STYLES = {
  invited: "text-purple-800 bg-purple-100 border-purple-300",
  booked: "text-sky-800 bg-sky-100 border-sky-300",
  arrived: "text-emerald-800 bg-emerald-100 border-emerald-300",
  no_show: "text-red-700 bg-red-100 border-red-300",
  cancelled: "text-slate-600 bg-slate-100 border-slate-300",
  rescheduled: "text-slate-600 bg-slate-100 border-slate-300",
  waiting: "text-amber-800 bg-amber-100 border-amber-300",
  declined_invite: "text-slate-600 bg-slate-100 border-slate-300",
  completed: "text-green-800 bg-green-100 border-green-300",
};

export function BookingStatusPill({ status }) {
  if (!status) return <span className="text-muted-foreground text-xs">—</span>;
  const cls = BOOKING_STATUS_STYLES[status] || "text-slate-600 bg-slate-100 border-slate-300";
  return (
    <Badge variant="outline" className={`font-mono text-[10px] uppercase tracking-widest ${cls}`} data-testid={`booking-status-${status}`}>
      {status.replace(/_/g, " ")}
    </Badge>
  );
}

export const EVENT_STATUS_STYLES = {
  draft: "text-slate-600 bg-slate-100 border-slate-300",
  open: "text-emerald-800 bg-emerald-100 border-emerald-300",
  full: "text-amber-800 bg-amber-100 border-amber-300",
  closed: "text-slate-600 bg-slate-100 border-slate-300",
  completed: "text-green-800 bg-green-100 border-green-300",
  cancelled: "text-red-700 bg-red-100 border-red-300",
};

export function EventStatusPill({ status }) {
  const cls = EVENT_STATUS_STYLES[status] || EVENT_STATUS_STYLES.draft;
  return (
    <Badge variant="outline" className={`font-mono text-[10px] uppercase tracking-widest ${cls}`} data-testid={`event-status-${status}`}>
      {status}
    </Badge>
  );
}
